/**
 * Onboarding mutations: the run lifecycle.
 *
 * NON-OPTIMISTIC: onboarding gates a hotel toward going live (activation), so
 * every step waits for the backend and re-syncs the run + (for item review) the
 * draft-items list. Most steps return the updated `OnboardingRun`; item review
 * returns the updated draft item.
 */

import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { unwrap } from '../queries/_helpers';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import {
  type OnboardingDraftItem,
  type OnboardingRun,
  onboardingDraftItemSchema,
  onboardingRunSchema,
} from '../responses';
import type {
  ActivationGateBody,
  CreateRunBody,
  RegisterRawPagesBody,
  ReviewDraftItemBody,
  SiteIngestResultBody,
  ValidateUrlBody,
} from './_types';

function useRunSync(tenantId: string, hotelId: string) {
  const queryClient = useQueryClient();
  return (run: OnboardingRun) => {
    queryClient.setQueryData(queryKeys.onboardingRun(tenantId, hotelId, run.id), run);
    void queryClient.invalidateQueries({ queryKey: queryKeys.onboardingRuns(tenantId, hotelId) });
  };
}

/** `POST /onboarding/runs` — start an onboarding run from a hotel URL. */
export function useCreateOnboardingRun(
  tenantId: string,
  hotelId: string,
): UseMutationResult<OnboardingRun, unknown, CreateRunBody> {
  const client = useApiClient();
  const sync = useRunSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async (body: CreateRunBody) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/onboarding/runs',
        { params: { path: { tenantId, hotelId } }, body, parseAs: 'json' },
      );
      return unwrap(result, onboardingRunSchema);
    },
    onSuccess: sync,
  });
}

export interface ValidateUrlVars {
  runId: string;
  body: ValidateUrlBody;
}

/** `POST /onboarding/runs/:runId/validate-url` — confirm domain ownership / URL. */
export function useValidateUrl(
  tenantId: string,
  hotelId: string,
): UseMutationResult<OnboardingRun, unknown, ValidateUrlVars> {
  const client = useApiClient();
  const sync = useRunSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async ({ runId, body }: ValidateUrlVars) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/onboarding/runs/{runId}/validate-url',
        { params: { path: { tenantId, hotelId, runId } }, body, parseAs: 'json' },
      );
      return unwrap(result, onboardingRunSchema);
    },
    onSuccess: sync,
  });
}

/** `POST /onboarding/runs/:runId/crawl` — enqueue the site crawl. */
export function useEnqueueCrawl(
  tenantId: string,
  hotelId: string,
): UseMutationResult<OnboardingRun, unknown, { runId: string }> {
  const client = useApiClient();
  const sync = useRunSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async ({ runId }: { runId: string }) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/onboarding/runs/{runId}/crawl',
        { params: { path: { tenantId, hotelId, runId } }, parseAs: 'json' },
      );
      return unwrap(result, onboardingRunSchema);
    },
    onSuccess: sync,
  });
}

export interface RegisterRawPagesVars {
  runId: string;
  body: RegisterRawPagesBody;
}

/** `POST /onboarding/runs/:runId/raw-pages` — register fetched raw pages. */
export function useRegisterRawPages(
  tenantId: string,
  hotelId: string,
): UseMutationResult<OnboardingRun, unknown, RegisterRawPagesVars> {
  const client = useApiClient();
  const sync = useRunSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async ({ runId, body }: RegisterRawPagesVars) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/onboarding/runs/{runId}/raw-pages',
        { params: { path: { tenantId, hotelId, runId } }, body, parseAs: 'json' },
      );
      return unwrap(result, onboardingRunSchema);
    },
    onSuccess: sync,
  });
}

export interface IngestVars {
  runId: string;
  body: SiteIngestResultBody;
}

/** `POST /onboarding/runs/:runId/ingest` — accept extracted site ingest results. */
export function useIngestSite(
  tenantId: string,
  hotelId: string,
): UseMutationResult<OnboardingRun, unknown, IngestVars> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const sync = useRunSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async ({ runId, body }: IngestVars) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/onboarding/runs/{runId}/ingest',
        { params: { path: { tenantId, hotelId, runId } }, body, parseAs: 'json' },
      );
      return unwrap(result, onboardingRunSchema);
    },
    onSuccess: (run) => {
      sync(run);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.onboardingDraftItems(tenantId, hotelId, run.id),
      });
    },
  });
}

export interface ReviewDraftItemVars {
  runId: string;
  itemId: string;
  body: ReviewDraftItemBody;
}

/** `POST /onboarding/runs/:runId/draft-items/:itemId/review` — accept/edit/reject an item. */
export function useReviewDraftItem(
  tenantId: string,
  hotelId: string,
): UseMutationResult<OnboardingDraftItem, unknown, ReviewDraftItemVars> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ runId, itemId, body }: ReviewDraftItemVars) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/onboarding/runs/{runId}/draft-items/{itemId}/review',
        { params: { path: { tenantId, hotelId, runId, itemId } }, body, parseAs: 'json' },
      );
      return unwrap(result, onboardingDraftItemSchema);
    },
    onSuccess: (_item, { runId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.onboardingDraftItems(tenantId, hotelId, runId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.onboardingRun(tenantId, hotelId, runId),
      });
    },
  });
}

/** `POST /onboarding/runs/:runId/approve` — approve the reviewed draft. */
export function useApproveRun(
  tenantId: string,
  hotelId: string,
): UseMutationResult<OnboardingRun, unknown, { runId: string }> {
  const client = useApiClient();
  const sync = useRunSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async ({ runId }: { runId: string }) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/onboarding/runs/{runId}/approve',
        { params: { path: { tenantId, hotelId, runId } }, parseAs: 'json' },
      );
      return unwrap(result, onboardingRunSchema);
    },
    onSuccess: sync,
  });
}

export interface ActivationGateVars {
  runId: string;
  body: ActivationGateBody;
}

/** `POST /onboarding/runs/:runId/activation-gate` — record the pre-activation gate result. */
export function useRecordActivationGate(
  tenantId: string,
  hotelId: string,
): UseMutationResult<OnboardingRun, unknown, ActivationGateVars> {
  const client = useApiClient();
  const sync = useRunSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async ({ runId, body }: ActivationGateVars) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/onboarding/runs/{runId}/activation-gate',
        { params: { path: { tenantId, hotelId, runId } }, body, parseAs: 'json' },
      );
      return unwrap(result, onboardingRunSchema);
    },
    onSuccess: sync,
  });
}

/** `POST /onboarding/runs/:runId/activate` — go live; also affects the hotel. */
export function useActivateHotel(
  tenantId: string,
  hotelId: string,
): UseMutationResult<OnboardingRun, unknown, { runId: string }> {
  const client = useApiClient();
  const queryClient = useQueryClient();
  const sync = useRunSync(tenantId, hotelId);
  return useMutation({
    mutationFn: async ({ runId }: { runId: string }) => {
      const result = await client.POST(
        '/api/v1/tenants/{tenantId}/hotels/{hotelId}/onboarding/runs/{runId}/activate',
        { params: { path: { tenantId, hotelId, runId } }, parseAs: 'json' },
      );
      return unwrap(result, onboardingRunSchema);
    },
    onSuccess: (run) => {
      sync(run);
      // Activation flips the hotel to active — refresh hotel reads.
      void queryClient.invalidateQueries({ queryKey: queryKeys.hotel(tenantId, hotelId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.hotels(tenantId) });
    },
  });
}
